'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  menu: Menu;
};

export function HeaderMain({ menu, generalLayout }: HeaderMainProps) {
  const pathname = usePathname();

  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const isHomepage = pathname === ROUTES.HOME();

  const isDesktop = useBreakpoint('sm');

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > (isDesktop ? 48 : 40));
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop]);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'custom-section',
          'border-b border-b-[#EEEEEE] transition-all',
          isScrolled && 'ea-header-main__scrolled bg-white',
          !isScrolled && isHomepage && 'bg-primary-foreground',
        )}
      >
        <div className={cn('custom-container--xxl', 'hidden items-center justify-between lg:flex')}>
          <nav className="flex items-center justify-start gap-8">
            {menu.fields.map(item => {
              const isActive = pathname === item.path;

              return (
                <Link
                  key={item.name}
                  prefetch={false}
                  href={getSafeHref(item?.path ?? '/')}
                  className={cn(
                    'relative flex h-[100px] items-center justify-center',
                    isActive && 'ea-header-main__active-item font-bold',
                  )}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <Link
            prefetch={false}
            href={'/'}
            className="cursor-pointer transition-all hover:opacity-75"
          >
            <Image
              src={generalLayout.header?.logo?.url ?? ''}
              alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company ?? '')}
              width={0}
              height={0}
              className="h-8 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center justify-end gap-8 lg:w-[385px]">
            <Link
              prefetch={false}
              href={`tel:${generalLayout?.contact?.phone}`}
              className="flex items-center justify-end gap-2 transition-all hover:opacity-80"
            >
              <Image
                src={'/images/phone.svg'}
                alt="Telefón"
                width={20}
                height={20}
                className="object-contain object-center"
              />
              {generalLayout?.contact?.phone}
            </Link>
            <Button asChild variant={'dark'}>
              <Link prefetch={false} href={'/rezervacia'}>
                Rezervácia
              </Link>
            </Button>
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-4 lg:hidden">
          <div className="flex items-center justify-start gap-4">
            <MobileSidebar menu={menu.fields} side="left" generalLayout={generalLayout} />
            <Link
              prefetch={false}
              href={'/'}
              className="cursor-pointer transition-all hover:opacity-75"
            >
              <Image
                src={generalLayout.header?.logo?.url ?? ''}
                alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company ?? '')}
                width={0}
                height={0}
                className="h-[19px] w-auto object-contain sm:h-6"
              />
            </Link>
          </div>
          <Button
            size={'none'}
            variant={'dark'}
            className="h-8 px-4 text-sm sm:h-10 sm:px-6 sm:text-base"
          >
            <Link prefetch={false} href={'/rezervacia'}>
              Rezervácia
            </Link>
          </Button>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
